import { useState, useRef, useCallback, useEffect } from 'react';
import { useStore } from '../store';
import { generateApi } from '../services/api';
import { imageApi } from '../services/imageApi';
import { Section, PAGE_LAYOUTS, PageLayout } from '../types';
import { IMAGE_PROVIDERS, ImageProvider } from '../types/images';
import {
  Sparkles,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  Copy,
  RefreshCw,
  Paperclip,
  ArrowUp,
  ArrowDown,
  Eraser,
  Save,
  Plus,
  Image as ImageIcon,
  Settings2,
  Layout,
  BarChart2,
} from 'lucide-react';
import clsx from 'clsx';
import InkSpreadText from './InkSpreadText';
import DataVizCreator from './visualizations/DataVizCreator';

interface Props {
  section: Section;
  sectionNumber?: number;
  onGenerate?: () => void;
  onRegenerate?: (selectedText: string, startIndex: number, endIndex: number) => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDelete?: () => void;
  onClear?: () => void;
  onAddAfter?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

export default function SectionCard({
  section,
  sectionNumber,
  onGenerate,
  onRegenerate,
  onMoveUp,
  onMoveDown,
  onDelete,
  onClear,
  onAddAfter,
  isFirst = false,
  isLast = false,
}: Props) {
  const {
    updateSection,
    removeSection,
    applicantData,
    genericSections,
    localFiles,
    selectedDocuments,
    systemPrompt,
    nuanceSettings,
    referenceText,
    styleOptions,
    addNotification,
    addImageBlock,
    imageProvider,
    currentSessionId,
    hasUnsavedChanges,
    saveCurrentSession,
  } = useStore();

  const [editingTitle, setEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(section.title);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [showDocAttach, setShowDocAttach] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(!section.content); // Open if no content yet
  const [showDataVizCreator, setShowDataVizCreator] = useState(false);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const contentDisplayRef = useRef<HTMLDivElement>(null);

  // Auto-collapse settings when generation starts
  useEffect(() => {
    if (section.isGenerating) {
      setIsSettingsOpen(false);
    }
  }, [section.isGenerating]);

  // Get image dimensions based on size setting
  const getImageDimensions = (size: 'square' | 'portrait' | 'landscape', provider: ImageProvider) => {
    const providerConfig = IMAGE_PROVIDERS[provider];
    const sizes = providerConfig?.supportedSizes || [];

    switch (size) {
      case 'portrait':
        return sizes.find(s => s.height > s.width) || { width: 1024, height: 1792 };
      case 'landscape':
        return sizes.find(s => s.width > s.height) || { width: 1792, height: 1024 };
      case 'square':
      default:
        return sizes.find(s => s.width === s.height) || { width: 1024, height: 1024 };
    }
  };

  // Auto-generate image prompt from content
  const generateImagePrompt = (content: string, title: string): string => {
    // Extract first sentence or first 100 characters as context
    const firstSentence = content.split(/[.!?]/)[0]?.trim() || '';
    const context = firstSentence.length > 100 ? firstSentence.substring(0, 100) : firstSentence;
    return `Professional illustration for a section titled "${title}". Context: ${context}. Style: clean, modern, professional.`;
  };

  const handleGenerate = async () => {
    updateSection(section.id, { isGenerating: true });

    try {
      const genericFields: Record<string, Record<string, string>> = {};
      const titleCount: Record<string, number> = {};
      genericSections.forEach((gs) => {
        let sectionKey = gs.title;
        titleCount[gs.title] = (titleCount[gs.title] || 0) + 1;
        if (titleCount[gs.title] > 1) {
          sectionKey = `${gs.title} (${titleCount[gs.title]})`;
        }
        genericFields[sectionKey] = {};
        const labelCount: Record<string, number> = {};
        gs.fields.forEach((field) => {
          if (field.value) {
            let fieldKey = field.label;
            labelCount[field.label] = (labelCount[field.label] || 0) + 1;
            if (labelCount[field.label] > 1) {
              fieldKey = `${field.label} (${labelCount[field.label]})`;
            }
            genericFields[sectionKey][fieldKey] = field.value;
          }
        });
      });

      // Build document contents from section-specific documents
      const sectionDocContents = (section.documents || [])
        .filter((d: any) => d.content)
        .map((d: any) => ({ filename: d.filename || d.name || 'Document', content: d.content }));

      // Build document contents from global context documents
      const globalDocContents = (selectedDocuments || [])
        .filter((d: any) => d.content)
        .map((d: any) => ({ filename: d.filename || d.name || 'Document', content: d.content }));

      const result = await generateApi.generate({
        section: {
          id: section.id,
          title: section.title,
          prompt: section.prompt,
          documents: section.documents,
          role: section.role,
        },
        applicantData: {
          ...applicantData,
          genericFields,
        },
        selectedDocuments: selectedDocuments || [],
        documentContents: [...globalDocContents, ...sectionDocContents],
        systemPrompt: systemPrompt || undefined,
        llmModel: applicantData.llmModel || undefined,
        referenceText: referenceText || undefined,
        styleOptions: referenceText ? styleOptions : undefined,
        nuanceSettings,
      });

      if (result.success) {
        updateSection(section.id, { content: result.content });
        addNotification('Content generated', 'success');

        // Check if image generation is enabled
        if (section.imageConfig?.enabled) {
          updateSection(section.id, { isGenerating: false, isGeneratingImage: true });

          try {
            // Use custom prompt or auto-generate from content
            const imagePrompt = section.imageConfig.prompt?.trim()
              || generateImagePrompt(result.content, section.title);

            const dimensions = getImageDimensions(
              section.imageConfig.size || 'square',
              section.imageConfig.provider || 'openai'
            );

            const imageResult = await imageApi.generate({
              prompt: imagePrompt,
              provider: section.imageConfig.provider || 'openai',
              width: dimensions.width,
              height: dimensions.height,
            });

            if (imageResult.success && imageResult.image) {
              // Add image locally first
              const tempBlock = {
                id: `temp-${Date.now()}`,
                sectionId: section.id,
                position: 'after' as const,
                imageUrl: imageResult.image.url,
                width: 'medium' as const,
                alignment: 'center' as const,
                generatedPrompt: imagePrompt,
                provider: section.imageConfig.provider,
              };
              addImageBlock(section.id, tempBlock);
              addNotification('Image generated', 'success');

              // Try to save to backend if session exists
              if (currentSessionId) {
                try {
                  if (hasUnsavedChanges) {
                    await saveCurrentSession();
                  }
                  await imageApi.saveImageBlock(currentSessionId, {
                    sectionId: section.id,
                    position: 'after',
                    imageUrl: imageResult.image.url,
                    width: 'medium',
                    alignment: 'center',
                    generatedPrompt: imagePrompt,
                    provider: section.imageConfig.provider,
                  });
                } catch (saveErr) {
                  console.warn('Could not save image to session:', saveErr);
                }
              }
            }
          } catch (imgErr: any) {
            console.error('Image generation failed:', imgErr);
            addNotification(imgErr.message || 'Image generation failed', 'error');
          } finally {
            updateSection(section.id, { isGeneratingImage: false });
          }
        }
      } else {
        addNotification(result.error || 'Generation failed', 'error');
      }
    } catch (err: any) {
      console.error('Content generation error:', err);
      let errorMsg = 'Generation failed';

      if (err.response?.status === 504 || err.code === 'ECONNABORTED') {
        errorMsg = 'Request timed out - check if backend is running and API key is configured';
      } else if (err.response?.status === 400) {
        errorMsg = err.response?.data?.error || 'Missing API key - add one in Settings';
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      } else if (err.message) {
        errorMsg = err.message;
      }

      addNotification(errorMsg, 'error');
    } finally {
      updateSection(section.id, { isGenerating: false, isGeneratingImage: false });
    }

    if (onGenerate) onGenerate();
  };

  const handleSaveTitle = () => {
    updateSection(section.id, { title: tempTitle });
    setEditingTitle(false);
  };


  const handleCopy = () => {
    navigator.clipboard.writeText(section.content);
    addNotification('Copied to clipboard', 'success');
  };


  const handleTextSelection = useCallback(() => {
    if (contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      if (start !== end) {
        const selected = section.content.substring(start, end);
        setSelectedText(selected);
      } else {
        setSelectedText('');
      }
    }
  }, [section.content]);

  const handleRegenerateSelected = () => {
    if (selectedText && onRegenerate && contentRef.current) {
      const start = contentRef.current.selectionStart;
      const end = contentRef.current.selectionEnd;
      onRegenerate(selectedText, start, end);
    } else {
      handleGenerate();
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    } else {
      removeSection(section.id);
    }
  };

  const handleClear = () => {
    updateSection(section.id, { content: '' });
    addNotification('Content cleared', 'info');
    if (onClear) onClear();
  };

  const handleMoveUp = () => {
    if (onMoveUp) onMoveUp();
  };

  const handleMoveDown = () => {
    if (onMoveDown) onMoveDown();
  };

  const handleAttachDocument = (doc: any) => {
    const alreadyAttached = section.documents.some((d) => d.id === doc.id);
    if (alreadyAttached) {
      handleDetachDocument(doc.id);
    } else {
      updateSection(section.id, { documents: [...section.documents, doc] });
      addNotification('Document attached', 'success');
    }
    setShowDocAttach(false);
  };

  const handleDetachDocument = (docId: string) => {
    const updatedDocs = section.documents.filter((d) => d.id !== docId);
    updateSection(section.id, { documents: updatedDocs });
    addNotification('Document detached', 'info');
  };

  const toggleEditMode = () => {
    setIsEditingContent(!isEditingContent);
  };

  return (
    <div className="section-card-v2">
      {/* Slim Header Bar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 rounded-t-lg">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {sectionNumber !== undefined && (
            <span className="w-6 h-6 flex items-center justify-center bg-accent text-white text-xs font-bold rounded-full flex-shrink-0">
              {sectionNumber}
            </span>
          )}

          {editingTitle ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                className="input text-sm flex-1 py-1"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') setEditingTitle(false);
                }}
              />
              <button onClick={handleSaveTitle} className="p-1 text-green-600 hover:bg-green-50 rounded">
                <Check size={14} />
              </button>
              <button onClick={() => setEditingTitle(false)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                <X size={14} />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0">
              <h3 className="font-medium text-gray-900 text-sm truncate">{section.title}</h3>
              <button
                onClick={() => {
                  setTempTitle(section.title);
                  setEditingTitle(true);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded flex-shrink-0"
              >
                <Edit2 size={12} />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Section Role */}
          <select
            value={section.role || ''}
            onChange={(e) => updateSection(section.id, { role: e.target.value || undefined })}
            className="text-[10px] text-gray-500 border border-gray-200 rounded px-1 py-0.5 bg-white hover:border-gray-300 max-w-[70px]"
            title="Section role"
          >
            <option value="">Role</option>
            <option value="introduction">Intro</option>
            <option value="body">Body</option>
            <option value="conclusion">Conclusion</option>
            <option value="transition">Transition</option>
            <option value="summary">Summary</option>
          </select>

          {/* Settings Toggle */}
          <button
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            className={clsx(
              'p-1.5 rounded transition-colors',
              isSettingsOpen
                ? 'bg-accent text-white'
                : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
            )}
            title={isSettingsOpen ? 'Hide settings' : 'Show settings'}
          >
            <Settings2 size={14} />
          </button>

          {/* Move Up/Down */}
          <button
            onClick={handleMoveUp}
            disabled={isFirst}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move up"
          >
            <ArrowUp size={14} />
          </button>
          <button
            onClick={handleMoveDown}
            disabled={isLast}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Move down"
          >
            <ArrowDown size={14} />
          </button>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={section.isGenerating || section.isGeneratingImage}
            className={clsx(
              'flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors',
              section.isGenerating || section.isGeneratingImage
                ? 'bg-gray-200 text-gray-400'
                : 'bg-accent text-white hover:bg-accent-dark'
            )}
          >
            {section.isGenerating ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Writing...</span>
              </>
            ) : section.isGeneratingImage ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                <span className="hidden sm:inline">Creating Image...</span>
              </>
            ) : (
              <>
                <Sparkles size={12} />
                <span className="hidden sm:inline">
                  {section.imageConfig?.enabled ? 'Generate All' : 'Generate'}
                </span>
              </>
            )}
          </button>


          {/* Add Section After */}
          {onAddAfter && (
            <button
              onClick={onAddAfter}
              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
              title="Add section after"
            >
              <Plus size={14} />
            </button>
          )}

          {/* Delete */}
          <button
            onClick={handleDelete}
            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
            title="Delete section"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Collapsible Settings Panel */}
      <div
        className={clsx(
          'transition-all duration-300 ease-in-out bg-gray-50 border-b border-gray-200',
          isSettingsOpen ? 'max-h-[700px] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0 overflow-hidden'
        )}
      >
        <div className="p-3 space-y-3">
          {/* AI Prompt */}
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase mb-1.5 block">
              AI Prompt
            </label>
            <textarea
              value={section.prompt}
              onChange={(e) => updateSection(section.id, { prompt: e.target.value })}
              className="input text-sm w-full"
              rows={2}
              placeholder="Enter AI instructions for this section..."
            />
          </div>

          {/* Image Generation */}
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1.5">
              <ImageIcon size={10} />
              Generate Image
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={section.imageConfig?.enabled || false}
                onChange={(e) =>
                  updateSection(section.id, {
                    imageConfig: {
                      ...section.imageConfig,
                      enabled: e.target.checked,
                      provider: section.imageConfig?.provider || imageProvider || 'openai',
                      size: section.imageConfig?.size || 'square',
                    },
                  })
                }
                className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
              />
              <span className="text-xs text-gray-600">With content</span>
            </label>
          </div>

          {section.imageConfig?.enabled && (
            <div className="space-y-2 py-2">
              <div className="flex gap-2">
                <select
                  value={section.imageConfig.provider || 'openai'}
                  onChange={(e) =>
                    updateSection(section.id, {
                      imageConfig: {
                        ...section.imageConfig!,
                        provider: e.target.value as ImageProvider,
                      },
                    })
                  }
                  className="input text-xs flex-1 py-1"
                >
                  <option value="openai">DALL-E 3</option>
                  <option value="stability">Stability AI</option>
                  <option value="leonardo">Leonardo AI</option>
                  <option value="gemini">Google Imagen</option>
                </select>
                <select
                  value={section.imageConfig.size || 'square'}
                  onChange={(e) =>
                    updateSection(section.id, {
                      imageConfig: {
                        ...section.imageConfig!,
                        size: e.target.value as 'square' | 'portrait' | 'landscape',
                      },
                    })
                  }
                  className="input text-xs py-1 w-24"
                >
                  <option value="square">Square</option>
                  <option value="portrait">Portrait</option>
                  <option value="landscape">Landscape</option>
                </select>
              </div>
              <textarea
                value={section.imageConfig.prompt || ''}
                onChange={(e) =>
                  updateSection(section.id, {
                    imageConfig: {
                      ...section.imageConfig!,
                      prompt: e.target.value,
                    },
                  })
                }
                className="input text-xs w-full"
                rows={2}
                placeholder="Image prompt (optional - auto-generates from content if empty)"
              />
            </div>
          )}

          {/* Data Visualization */}
          <div className="py-2 border-t border-gray-200">
            <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1.5 mb-2">
              <BarChart2 size={10} />
              Data Visualization
            </label>
            <button
              onClick={() => setShowDataVizCreator(true)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-accent border border-accent/30 rounded-lg hover:bg-accent/5 transition-colors"
            >
              <Plus size={12} />
              Add Chart or Table
            </button>
          </div>

          {/* Section Documents */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1">
                <Paperclip size={10} />
                Section Documents ({section.documents.length})
              </label>
              <button
                onClick={() => setShowDocAttach(!showDocAttach)}
                className="text-xs text-accent hover:text-accent-dark flex items-center gap-1 px-2 py-0.5 bg-accent/10 hover:bg-accent/20 rounded transition-colors"
              >
                <Plus size={10} />
                Attach
              </button>
            </div>

            {/* Attach dropdown */}
            {showDocAttach && (
              <div className="mb-2 border border-gray-200 rounded-md max-h-24 overflow-y-auto bg-white">
                {(localFiles || []).length === 0 && selectedDocuments.length === 0 ? (
                  <p className="text-xs text-gray-400 px-2 py-2 text-center">
                    No files available. Upload files in the Documents tab.
                  </p>
                ) : (
                  [...(localFiles || []), ...selectedDocuments]
                    .filter((file, index, self) => self.findIndex((f) => f.id === file.id) === index)
                    .map((doc) => {
                      const filename = 'filename' in doc ? doc.filename : ('name' in doc ? (doc as any).name : 'Document');
                      const isAttached = section.documents.some((d) => d.id === doc.id);
                      return (
                        <button
                          key={doc.id}
                          onClick={() => handleAttachDocument(doc)}
                          className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 text-left border-b border-gray-100 last:border-b-0"
                        >
                          {isAttached ? (
                            <Check size={10} className="text-green-600 flex-shrink-0" />
                          ) : (
                            <Paperclip size={10} className="text-gray-400 flex-shrink-0" />
                          )}
                          <span className="truncate">{filename}</span>
                        </button>
                      );
                    })
                )}
              </div>
            )}

            {/* Document pills */}
            {section.documents.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {section.documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center gap-1 px-2 py-0.5 bg-accent/10 text-accent rounded text-xs"
                  >
                    <Paperclip size={8} />
                    <span className="max-w-[100px] truncate">
                      {'filename' in doc ? doc.filename : ('name' in doc ? doc.name : 'Document')}
                    </span>
                    <button
                      onClick={() => handleDetachDocument(doc.id)}
                      className="p-0.5 hover:bg-accent/20 rounded"
                    >
                      <X size={8} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-2 text-xs text-gray-400 border border-dashed border-gray-200 rounded bg-white">
                No documents attached
              </div>
            )}
          </div>

          {/* Page Layout */}
          <div>
            <label className="text-xs font-medium text-gray-600 uppercase mb-1.5 flex items-center gap-1">
              <Layout size={10} />
              Page Layout
            </label>
            <select
              value={section.layout || 'full-width'}
              onChange={(e) => updateSection(section.id, { layout: e.target.value as PageLayout })}
              className="input text-sm w-full py-1.5"
            >
              {PAGE_LAYOUTS.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name} - {layout.description}
                </option>
              ))}
            </select>
          </div>

        </div>
      </div>

      {/* Content Area - The Main Stage */}
      <div className="p-4 min-h-[120px]">
        {section.isGenerating ? (
          /* Generating State - Ink Spread Effect */
          <div className="prose prose-sm max-w-none">
            <InkSpreadText
              text={section.content}
              isStreaming={true}
              className="whitespace-pre-wrap text-gray-800"
            />
          </div>
        ) : section.isGeneratingImage ? (
          /* Image Generation State */
          <div>
            <div className="prose prose-sm max-w-none mb-4">
              <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                {section.content}
              </div>
            </div>
            <div className="flex items-center justify-center p-6 border-2 border-dashed border-accent/40 rounded-lg bg-accent/5">
              <div className="flex items-center gap-3">
                <Loader2 size={20} className="animate-spin text-accent" />
                <span className="text-sm text-accent font-medium">Generating image...</span>
              </div>
            </div>
          </div>
        ) : section.content ? (
          /* Content Display / Edit Mode */
          <div>
            {/* Content Actions Bar */}
            <div className="flex items-center justify-end gap-2 mb-2 -mt-1">
              <button
                onClick={toggleEditMode}
                className={clsx(
                  'text-xs flex items-center gap-1 px-2 py-0.5 rounded transition-colors',
                  isEditingContent
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                )}
              >
                {isEditingContent ? <Save size={10} /> : <Edit2 size={10} />}
                {isEditingContent ? 'Done' : 'Edit'}
              </button>
              <button
                onClick={handleCopy}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 hover:bg-gray-100 px-2 py-0.5 rounded"
              >
                <Copy size={10} />
                Copy
              </button>
              <button
                onClick={handleRegenerateSelected}
                disabled={section.isGenerating}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 hover:bg-gray-100 px-2 py-0.5 rounded"
                title={selectedText ? 'Regenerate selected' : 'Regenerate all'}
              >
                <RefreshCw size={10} />
                {selectedText ? 'Regen Selected' : 'Regen'}
              </button>
              <button
                onClick={handleClear}
                className="text-xs text-gray-400 hover:text-red-600 flex items-center gap-1 hover:bg-red-50 px-2 py-0.5 rounded"
              >
                <Eraser size={10} />
                Clear
              </button>
            </div>

            {/* Selection indicator */}
            {selectedText && (
              <div className="mb-2 px-2 py-1 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                Selected: &quot;{selectedText.length > 50 ? selectedText.substring(0, 50) + '...' : selectedText}&quot;
              </div>
            )}

            {isEditingContent ? (
              /* Edit Mode - Textarea */
              <textarea
                ref={contentRef}
                value={section.content}
                onChange={(e) => updateSection(section.id, { content: e.target.value })}
                onMouseUp={handleTextSelection}
                onKeyUp={handleTextSelection}
                className="input text-sm w-full min-h-[200px] leading-relaxed"
                autoFocus
              />
            ) : (
              /* View Mode - Rendered Content */
              <div
                ref={contentDisplayRef}
                className="prose prose-sm max-w-none cursor-text"
                onClick={() => setIsEditingContent(true)}
              >
                <div className="whitespace-pre-wrap text-gray-800 leading-relaxed">
                  {section.content}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-8 text-gray-400">
            <Sparkles size={24} className="mb-2 opacity-50" />
            <p className="text-sm">Click <span className="font-medium text-accent">Generate</span> to create content</p>
            <p className="text-xs mt-1">or open settings to configure the prompt</p>
          </div>
        )}
      </div>

      {/* Data Visualization Creator Modal */}
      {showDataVizCreator && (
        <DataVizCreator
          sectionId={section.id}
          onClose={() => setShowDataVizCreator(false)}
        />
      )}
    </div>
  );
}
